import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { getMeasurementType, useCreateVehicle, useUpdateVehicle } from './api';
import { vehicleFormSchema, type VehicleFormInput } from './schemas';
import {
  FUEL_TYPE_LABELS,
  VEHICLE_CATEGORY_LABELS,
  type FuelType,
  type Vehicle,
  type VehicleCategory,
} from './types';

interface VehicleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle?: Vehicle | null;
}

const DEFAULTS: VehicleFormInput = {
  plate: '',
  brand: '',
  model: '',
  year: undefined,
  color: '',
  renavam: '',
  chassi: '',
  fuelType: 'flex',
  category: 'car',
  tankCapacityL: undefined,
  odometerKm: undefined,
  crlvNumber: '',
  crlvExpiresAt: '',
  anttRntrc: '',
  notes: '',
  measurementType: 'odometer',
};

function formatDateInput(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function VehicleFormDialog({ open, onOpenChange, vehicle }: VehicleFormDialogProps) {
  const isEdit = !!vehicle;
  const createMutation = useCreateVehicle();
  const updateMutation = useUpdateVehicle(vehicle?.id ?? '');

  const form = useForm<VehicleFormInput>({
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: DEFAULTS,
  });

  // Preenche os campos ao abrir em modo edição
  useEffect(() => {
    if (!open) return;
    if (vehicle) {
      form.reset({
        plate: vehicle.plate,
        brand: vehicle.brand ?? '',
        model: vehicle.model ?? '',
        year: vehicle.year ?? undefined,
        color: vehicle.color ?? '',
        renavam: vehicle.renavam ?? '',
        chassi: vehicle.chassi ?? '',
        fuelType: vehicle.fuelType,
        category: vehicle.category,
        tankCapacityL:
          vehicle.tankCapacityMl != null ? vehicle.tankCapacityMl / 1000 : undefined,
        odometerKm: vehicle.odometerMeters != null ? vehicle.odometerMeters / 1000 : 0,
        crlvNumber: vehicle.crlvNumber ?? '',
        crlvExpiresAt: formatDateInput(vehicle.crlvExpiresAt),
        anttRntrc: vehicle.anttRntrc ?? '',
        notes: vehicle.notes ?? '',
        measurementType: getMeasurementType(
          vehicle.tracksOdometer,
          vehicle.tracksHourmeter,
        ),
      });
    } else {
      form.reset(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicle?.id]);

  const onSubmit = async (values: VehicleFormInput) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(values);
        toast.success('Veículo atualizado');
      } else {
        await createMutation.mutateAsync(values);
        toast.success('Veículo cadastrado');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const submitting = form.formState.isSubmitting;
  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar veículo' : 'Novo veículo'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize os dados do veículo. Todos os campos podem ser alterados.'
              : 'Preencha os dados básicos. Você pode completar depois.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {rootError && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          )}

          {/* Identificação */}
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Identificação
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Placa *" error={form.formState.errors.plate?.message}>
                <Input
                  {...form.register('plate')}
                  placeholder="ABC1D23"
                  maxLength={8}
                  className="uppercase"
                  aria-invalid={!!form.formState.errors.plate}
                />
              </Field>
              <Field label="Marca" error={form.formState.errors.brand?.message} className="sm:col-span-1">
                <Input {...form.register('brand')} placeholder="Volkswagen" />
              </Field>
              <Field label="Modelo" error={form.formState.errors.model?.message}>
                <Input {...form.register('model')} placeholder="Polo" />
              </Field>
              <Field label="Ano" error={form.formState.errors.year?.message}>
                <Input
                  {...form.register('year', { valueAsNumber: true })}
                  type="number"
                  inputMode="numeric"
                  placeholder="2024"
                />
              </Field>
              <Field label="Cor" error={form.formState.errors.color?.message}>
                <Input {...form.register('color')} placeholder="Prata" />
              </Field>
              <Field label="Renavam" error={form.formState.errors.renavam?.message}>
                <Input {...form.register('renavam')} inputMode="numeric" />
              </Field>
            </div>
          </section>

          {/* Classificação */}
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Classificação
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Categoria" error={form.formState.errors.category?.message}>
                <Controller
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as VehicleCategory)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(VEHICLE_CATEGORY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>

              <Field label="Combustível" error={form.formState.errors.fuelType?.message}>
                <Controller
                  control={form.control}
                  name="fuelType"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as FuelType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(FUEL_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Tanque (litros)" error={form.formState.errors.tankCapacityL?.message}>
                <Input
                  {...form.register('tankCapacityL', { valueAsNumber: true })}
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  placeholder="50"
                />
              </Field>
              <Field
                label={
                  form.watch('measurementType') === 'hourmeter'
                    ? 'Horímetro (horas)'
                    : 'Odômetro (km)'
                }
                error={form.formState.errors.odometerKm?.message}
              >
                <Input
                  {...form.register('odometerKm', { valueAsNumber: true })}
                  type="number"
                  inputMode="numeric"
                  placeholder={
                    form.watch('measurementType') === 'hourmeter' ? '1200' : '12345'
                  }
                />
              </Field>
            </div>

            <Field
              label="Tipo de medição"
              error={form.formState.errors.measurementType?.message}
            >
              <Controller
                control={form.control}
                name="measurementType"
                render={({ field }) => (
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      {
                        v: 'odometer' as const,
                        title: 'Hodômetro',
                        desc: 'Distância em km (carros, caminhões)',
                      },
                      {
                        v: 'hourmeter' as const,
                        title: 'Horímetro',
                        desc: 'Tempo de uso em horas (máquinas)',
                      },
                      {
                        v: 'both' as const,
                        title: 'Ambos',
                        desc: 'Hodômetro + horímetro',
                      },
                    ].map((opt) => {
                      const active = field.value === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => field.onChange(opt.v)}
                          className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${
                            active
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:bg-accent/40'
                          }`}
                        >
                          <span
                            className={`text-xs font-semibold ${
                              active ? 'text-primary' : ''
                            }`}
                          >
                            {opt.title}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {opt.desc}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              />
            </Field>
          </section>

          {/* Documentação */}
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Documentação
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="CRLV (número)" error={form.formState.errors.crlvNumber?.message}>
                <Input {...form.register('crlvNumber')} />
              </Field>
              <Field label="Vencimento CRLV" error={form.formState.errors.crlvExpiresAt?.message}>
                <Input {...form.register('crlvExpiresAt')} type="date" />
              </Field>
              <Field
                label="Chassi"
                error={form.formState.errors.chassi?.message}
                className="sm:col-span-2"
              >
                <Input {...form.register('chassi')} />
              </Field>
              <Field
                label="RNTRC (ANTT)"
                error={form.formState.errors.anttRntrc?.message}
                className="sm:col-span-2"
              >
                <Input {...form.register('anttRntrc')} />
              </Field>
            </div>
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? 'Salvar alterações' : 'Cadastrar veículo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, error, className, children }: FieldProps) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
